import {
  EVENT_NAME,
  EventProps,
  trackAnalyticsEvent,
} from "@leagueplatform/analytics";
import {
  Box,
  Button,
  GDSStyleObject,
  Icon,
  queryHelpers,
  StackLayout,
  TextAction,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { useNavigateLegacy as useNavigate } from "@leagueplatform/routing";

export type ModalScreenHeaderProps = {
  /** Visible text rendered next to the back arrow. */
  backLabel: string;
  /** Route the close (X) button navigates to. */
  closeTo: string;
  /** Optional analytics context merged into both button click events. */
  analyticsEventProps?: EventProps;
  css?: GDSStyleObject;
};

/**
 * Top navigation row used by full-screen, modal-like flows: a labeled back
 * link on the left (navigates to the previous page) and a close button on
 * the right (navigates to a specific route).
 */
export const ModalScreenHeader = ({
  backLabel,
  closeTo,
  analyticsEventProps,
  css,
}: ModalScreenHeaderProps) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down("tablet"));

  const handleBackClick = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      detail: "back arrow button",
      ...analyticsEventProps,
    });
    navigate(-1);
  };

  const handleCloseClick = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      detail: "close button",
      ...analyticsEventProps,
    });
    navigate(closeTo);
  };

  return (
    <Box
      css={{
        marginTop: isMobile ? 0 : "48px",
        height: isMobile ? "48px" : "32px",
        ...css,
      }}
    >
      <StackLayout
        orientation="horizontal"
        horizontalAlignment="spaceBetween"
        verticalAlignment="center"
        css={{ height: "100%" }}
      >
        <TextAction
          as="button"
          onClick={handleBackClick}
          css={{
            display: "flex",
            alignItems: "center",
            gap: "$quarter",
            color: "$interactiveActionPrimary",
            "&:hover": {
              color: "$interactiveActionHovered",
            },
          }}
        >
          <Icon icon="interfaceArrowLeft" size="$one" aria-hidden="true" />
          {backLabel}
        </TextAction>
        <Button
          priority="tertiary"
          icon="tinyClose"
          size="small"
          hideLabel
          quiet
          onClick={handleCloseClick}
        >
          {formatMessage({ id: "STR_CLOSE" })}
        </Button>
      </StackLayout>
    </Box>
  );
};
